import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { EditorState, ReadonlyTransaction } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';

import { ACTION_CLOSE_POPUP, ACTION_OPEN_POPUP } from './actions';
import { pluginKey } from './plugin-key';

export const AI_IMAGE_GENERATION_ANCHOR_ATTR = 'data-ai-image-generation-anchor';
const ANCHOR_DECORATION_KEY = 'ai-image-generation-anchor';

export type AIImageGenerationPMPluginState = {
	decorations: DecorationSet;
	isOpen: boolean;
};

const createAnchorWidget = (pos: number): Decoration =>
	Decoration.widget(
		pos,
		() => {
			const el = document.createElement('span');
			el.setAttribute(AI_IMAGE_GENERATION_ANCHOR_ATTR, 'true');
			el.contentEditable = 'false';
			el.style.cssText =
				'display: inline-block; width: 0; height: 0; vertical-align: baseline; pointer-events: none;';
			return el;
		},
		{ side: -1, key: ANCHOR_DECORATION_KEY },
	);

export const createImageGenPlugin = (): SafePlugin<AIImageGenerationPMPluginState> => {
	return new SafePlugin({
		state: {
			init(): AIImageGenerationPMPluginState {
				return {
					isOpen: false,
					decorations: DecorationSet.empty,
				};
			},

			apply(
				tr: ReadonlyTransaction,
				pluginState: AIImageGenerationPMPluginState,
			): AIImageGenerationPMPluginState {
				const meta = tr.getMeta(pluginKey);
				const mappedDecorations = pluginState.decorations.map(tr.mapping, tr.doc);

				switch (meta?.type) {
					case ACTION_OPEN_POPUP:
						return {
							isOpen: true,
							decorations: DecorationSet.create(tr.doc, [createAnchorWidget(tr.selection.from)]),
						};
					case ACTION_CLOSE_POPUP:
						return { isOpen: false, decorations: DecorationSet.empty };
					default: {
						// If a doc edit destroyed the anchor position, auto-close so the
						// popup can't render against a missing target.
						if (pluginState.isOpen && mappedDecorations.find().length === 0) {
							return { isOpen: false, decorations: DecorationSet.empty };
						}
						return { isOpen: pluginState.isOpen, decorations: mappedDecorations };
					}
				}
			},
		},
		key: pluginKey,
		props: {
			decorations(state: EditorState) {
				return pluginKey.getState(state)?.decorations;
			},
		},
	});
};
